import { UnknownOutputParams } from 'expo-router';
import { REFERRAL } from 'features/signup/constants';
import { getParam } from 'features/signup/utils/signupUtils';
import { useContext } from 'react';
import authenticatedFetch from 'services/rest/authenticatedFetch';
import {
  campaign as campaignParams,
  campaignAnalytics as campaignParamsAnalytics,
  OFFER_CODE_PARAM,
} from 'shared/constants/urlParams';
import {
  EnvironmentContext,
  EnvironmentContextProps,
  getAttributionLambdaUrl,
} from 'shared/providers/environment';

export const getCampaignObjectAnalytics = (
  isValidReferral: boolean,
  urlSearchParams: UnknownOutputParams,
  referralCode: string | undefined,
): AttributionsInputsAnalytics => {
  const campaign = {
    source: isValidReferral
      ? REFERRAL
      : (getParam(urlSearchParams, campaignParamsAnalytics.source) as string),
    medium: getParam(urlSearchParams, campaignParamsAnalytics.medium) as string,
    name: getParam(urlSearchParams, campaignParamsAnalytics.campaign) as string,
    content: isValidReferral
      ? referralCode
      : (getParam(urlSearchParams, campaignParamsAnalytics.content) as string),
    keyword: getParam(
      urlSearchParams,
      campaignParamsAnalytics.keyword,
    ) as string,
    channel: getParam(
      urlSearchParams,
      campaignParamsAnalytics.channel,
    ) as string,
    term: getParam(urlSearchParams, campaignParamsAnalytics.term) as string,
    cmpid: getParam(urlSearchParams, campaignParamsAnalytics.cmpid) as string,
    adgid: getParam(urlSearchParams, campaignParamsAnalytics.adgid) as string,
    adid: getParam(urlSearchParams, campaignParamsAnalytics.adid) as string,
    cmptyp: getParam(urlSearchParams, campaignParamsAnalytics.cmptyp) as string,
    intent: getParam(urlSearchParams, campaignParamsAnalytics.intent) as string,
    offerCode: getParam(urlSearchParams, OFFER_CODE_PARAM) as string,
  };
  const cleanData = Object.entries(campaign)
    .filter(([, value]) => !!value)
    .reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});
  return cleanData as AttributionsInputsAnalytics;
};

export interface AttributionsInputsAnalytics {
  source: string;
  content: string;
  name: string;
  medium: string;
  channel: string;
  keyword: string;
  term: string;
  cmpid: string;
  adgid: string;
  adid: string;
  cmptyp: string;
  intent: string;
  userId: string;
  offerCode?: string;
}

export const getCampaignObject = (
  isValidReferral: boolean,
  urlSearchParams: UnknownOutputParams,
  userId: string,
  referralCode: string | undefined,
): SubmitAttributionsInputs => {
  const campaign = {
    acquisitionSource: isValidReferral
      ? REFERRAL
      : (getParam(urlSearchParams, campaignParams.source) as string),
    acquisitionMedium: getParam(
      urlSearchParams,
      campaignParams.medium,
    ) as string,
    acquisitionCampaign: getParam(
      urlSearchParams,
      campaignParams.campaign,
    ) as string,
    acquisitionContent: isValidReferral
      ? referralCode
      : (getParam(urlSearchParams, campaignParams.content) as string),
    acquisitionKeyword: getParam(
      urlSearchParams,
      campaignParams.keyword,
    ) as string,
    acquisitionChannel: getParam(
      urlSearchParams,
      campaignParams.channel,
    ) as string,
    acquisitionTerm: getParam(urlSearchParams, campaignParams.term) as string,
    offerCode: getParam(urlSearchParams, OFFER_CODE_PARAM) as string,
    userId: userId,
  };
  const cleanData = Object.entries(campaign)
    .filter(([, value]) => !!value)
    .reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});
  return cleanData as SubmitAttributionsInputs;
};

export interface SubmitAttributionsInputs {
  acquisitionSource: string;
  acquisitionContent: string;
  acquisitionCampaign: string;
  acquisitionMedium: string;
  acquisitionChannel: string;
  acquisitionKeyword: string;
  acquisitionTerm: string;
  offerCode: string;
  userId: string;
}

const postSubmitAttributionsRequest = async (
  lambdaUrl: string,
  data: SubmitAttributionsInputs,
): Promise<Record<string, never>> => {
  const response = await authenticatedFetch(`${lambdaUrl}`, {
    method: 'POST',
    cache: 'no-cache',
    body: JSON.stringify({
      ...data,
    }),
  });
  return response.json() as Promise<Record<string, never>>;
};

const useSubmitAttributions = () => {
  const { currentEnv } =
    useContext<EnvironmentContextProps>(EnvironmentContext);
  const lambdaUrl = getAttributionLambdaUrl(currentEnv);

  const postSubmitAttributions = async (data: SubmitAttributionsInputs) => {
    return await postSubmitAttributionsRequest(lambdaUrl, data);
  };
  return { postSubmitAttributions };
};

export default useSubmitAttributions;
